import { useId, useState } from 'react'
import { XMarkIcon, ClipboardDocumentIcon, ClipboardDocumentCheckIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { contentRepurposingAPI } from '../services/api'
import type { Content, ContentFormat, QuickRepurposeResult } from '../types'
import useEscapeKey from '../hooks/useEscapeKey'
import Spinner from './Spinner'

export const CONTENT_FORMAT_LABELS: Record<ContentFormat, string> = {
  LONG_FORM_ARTICLE: 'Long-Form Article',
  SHORT_FORM_POST: 'Short Post',
  VIDEO_SCRIPT: 'Video Script',
  THREAD: 'Twitter Thread',
  EMAIL_NEWSLETTER: 'Email Newsletter',
  INFOGRAPHIC_TEXT: 'Infographic Text',
  PODCAST_SCRIPT: 'Podcast Script',
  CAROUSEL_SLIDES: 'Carousel Slides',
  SHORTS_CAPTION: 'Shorts/Reels Caption',
  BULLET_POINTS: 'Bullet Points',
}

const SOURCE_FORMAT_BY_CONTENT_TYPE: Record<Content['content_type'], ContentFormat> = {
  twitter: 'SHORT_FORM_POST',
  instagram: 'SHORT_FORM_POST',
  youtube: 'VIDEO_SCRIPT',
  tiktok: 'SHORTS_CAPTION',
  blog: 'LONG_FORM_ARTICLE',
}

const getOutputText = (output: QuickRepurposeResult['output']): string => {
  if (typeof output === 'string') return output
  if (Array.isArray(output)) return output.join('\n\n')
  return `${output.subject}\n\n${output.preview}\n\n${output.body}`
}

interface RepurposeModalProps {
  isOpen: boolean
  onClose: () => void
  content: Content
}

const RepurposeModal = ({ isOpen, onClose, content }: RepurposeModalProps) => {
  const sourceFormat = SOURCE_FORMAT_BY_CONTENT_TYPE[content.content_type]
  const targetOptions = (Object.keys(CONTENT_FORMAT_LABELS) as ContentFormat[]).filter(
    (format) => format !== sourceFormat
  )

  const [targetFormat, setTargetFormat] = useState<ContentFormat>(targetOptions[0])
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<QuickRepurposeResult | null>(null)
  const [copied, setCopied] = useState(false)
  const titleId = useId()

  const handleClose = () => {
    setResult(null)
    setCopied(false)
    onClose()
  }

  useEscapeKey(handleClose, isOpen)

  if (!isOpen) return null

  const handleTargetFormatChange = (format: ContentFormat) => {
    setTargetFormat(format)
    setResult(null)
    setCopied(false)
  }

  const handleRepurpose = async () => {
    setLoading(true)
    setCopied(false)
    try {
      const data = await contentRepurposingAPI.quickRepurpose({
        source_text: content.text,
        source_format: sourceFormat,
        target_format: targetFormat,
      })
      setResult(data)
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to repurpose content')
    } finally {
      setLoading(false)
    }
  }

  const handleCopy = async () => {
    if (!result) return
    try {
      await navigator.clipboard.writeText(getOutputText(result.output))
      setCopied(true)
      toast.success('Copied to clipboard')
    } catch {
      toast.error('Failed to copy to clipboard')
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div role="dialog" aria-modal="true" aria-labelledby={titleId} className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 id={titleId} className="text-xl font-bold text-gray-900">Repurpose Content</h2>
          <button
            onClick={handleClose}
            className="text-gray-600 hover:text-gray-800 transition-colors"
            aria-label="Close modal"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <p className="text-sm font-medium text-gray-700 mb-1">
              Original &middot; {CONTENT_FORMAT_LABELS[sourceFormat]}
            </p>
            <p className="text-sm text-gray-600 bg-gray-50 rounded-md p-3 line-clamp-3">
              {content.text}
            </p>
          </div>

          <div>
            <label htmlFor="repurpose-target-format" className="block text-sm font-medium text-gray-700 mb-2">
              Repurpose As
            </label>
            <select
              id="repurpose-target-format"
              value={targetFormat}
              onChange={(e) => handleTargetFormatChange(e.target.value as ContentFormat)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {targetOptions.map((format) => (
                <option key={format} value={format}>
                  {CONTENT_FORMAT_LABELS[format]}
                </option>
              ))}
            </select>
          </div>

          {result && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-gray-700">Result</p>
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700"
                >
                  {copied ? (
                    <ClipboardDocumentCheckIcon className="h-4 w-4" />
                  ) : (
                    <ClipboardDocumentIcon className="h-4 w-4" />
                  )}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>

              {Array.isArray(result.output) ? (
                <ul className="space-y-2">
                  {result.output.map((item, index) => (
                    <li
                      key={index}
                      className="text-sm text-gray-800 bg-gray-50 rounded-md p-3 whitespace-pre-wrap"
                    >
                      {item}
                    </li>
                  ))}
                </ul>
              ) : typeof result.output === 'string' ? (
                <p className="text-sm text-gray-800 bg-gray-50 rounded-md p-3 whitespace-pre-wrap">
                  {result.output}
                </p>
              ) : (
                <div className="text-sm text-gray-800 bg-gray-50 rounded-md p-3 space-y-2">
                  <p>
                    <span className="font-medium">Subject: </span>
                    {result.output.subject}
                  </p>
                  <p>
                    <span className="font-medium">Preview: </span>
                    {result.output.preview}
                  </p>
                  <p className="whitespace-pre-wrap">{result.output.body}</p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex gap-3 px-6 py-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors font-medium"
          >
            Close
          </button>
          <button
            type="button"
            onClick={handleRepurpose}
            disabled={loading}
            className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading && <Spinner size="sm" className="border-white border-t-transparent" />}
            {loading ? 'Repurposing...' : 'Repurpose'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default RepurposeModal
